import React from 'react';
import PropTypes from 'prop-types';
import SidebarBody from '../SidebarBody';
import styles from './AboutLocation.module.scss';
import { ESRI_FIELD_TYPES, LAYER_INFO_HIDDEN_KEYS, COLORS } from '../../../lib/constants';
import { stringToHash } from '../../../lib/utils';
import ExpandableDetail from '../ExpandableDetail';
import dayjs from 'dayjs';
import LayersIcon from '../../../assets/svg/LayersIcon';

function OverlayAtLocation(props) {

  const { overlay, feature, fields } = props;

  const label = overlay.name;

  return (
    <div className={styles.layerBox}>
      <ExpandableDetail
        label={label}
        title={`${overlay.name}`}
        isExpanded={true}
        style={{ padding: '12px 0px' }}
      >
        {Object.keys(feature.attributes).map((attrName, i) => {
          if (feature.attributes[attrName] && feature.attributes[attrName].toString().trim() !== '') {
            /*
              #TODO: I'm hoping in the near future this is handled by some sort of layer service
              rather than in this code, cuz I could see this getting out of hand very quickly.
              -- TJC - June 23, 2020
            */
            const keyAlias = fields[attrName].alias.trim();
            const keyType = fields[attrName].type;
            let content = feature.attributes[attrName];
            const urlMatcher = /((http|https):\/\/)/gi;
            if (LAYER_INFO_HIDDEN_KEYS.includes(keyAlias.toLowerCase())) return null;

            switch (keyType) {
              case ESRI_FIELD_TYPES.Date:
                content = dayjs.unix(content / 1000).format('MMM D, YYYY');
                break;
              case ESRI_FIELD_TYPES.Double:
                content = content.toFixed(2);
                break;
              case ESRI_FIELD_TYPES.GlobalID:
                return null;
              case ESRI_FIELD_TYPES.ObjectID:
                return null;
              default:
                break;
            }

            return (
              <div key={i} className={styles.field}>
                <div className={styles.fieldName}>{keyAlias}</div>
                <div className={styles.fieldValue}>
                  {keyType == ESRI_FIELD_TYPES.String && urlMatcher.test(content) ?
                    <a target="_blank" href={content} rel="noreferrer">{content.toString().trim()}</a> :
                    content.toString().trim()
                  }
                </div>
              </div>
            );
          }
          return null;
        })}
      </ExpandableDetail>
    </div>
  );
}

OverlayAtLocation.propTypes = {
  overlay: PropTypes.object,
  feature: PropTypes.object,
  fields: PropTypes.object,
};

function LayerAtLocation(props) {

  const { layer, feature, fields } = props;

  const label = layer.subCategory != null ? `${layer.category} - ${layer.subCategory}` : `${layer.category}`;

  return (
    <div className={styles.layerBox}>
      <ExpandableDetail
        label={label}
        title={`${layer.layerName}`}
        isExpanded={true}
        style={{ padding: '12px 0px' }}
      >
        {Object.keys(feature.attributes).map((attrName, i) => {
          if (feature.attributes[attrName] && feature.attributes[attrName].toString().trim() !== '') {
            /*
              #TODO: I'm hoping in the near future this is handled by some sort of layer service
              rather than in this code, cuz I could see this getting out of hand very quickly.
              -- TJC - June 23, 2020
            */
            const keyAlias = fields[attrName].alias.trim();
            const keyType = fields[attrName].type;
            let content = feature.attributes[attrName];
            const urlMatcher = /((http|https):\/\/)/gi;

            if (LAYER_INFO_HIDDEN_KEYS.includes(keyAlias.toLowerCase())) return null;

            switch (keyType) {
              case ESRI_FIELD_TYPES.Date:
                content = dayjs.unix(content / 1000).format('MMM D, YYYY');
                break;
              case ESRI_FIELD_TYPES.Double:
                content = content.toFixed(2);
                break;
              case ESRI_FIELD_TYPES.GlobalID:
                return null;
              case ESRI_FIELD_TYPES.ObjectID:
                return null;
              default:
                break;
            }

            return (
              <div key={i} className={styles.field}>
                <div className={styles.fieldName}>{keyAlias}</div>
                <div className={styles.fieldValue}>
                  {keyType == ESRI_FIELD_TYPES.String && urlMatcher.test(content) ?
                    <a target="_blank" href={content} rel="noreferrer">{content.toString().trim()}</a> :
                    content.toString().trim()
                  }
                </div>
              </div>
            );
          }
          return null;
        })}
      </ExpandableDetail>
    </div>
  );
}

LayerAtLocation.propTypes = {
  layer: PropTypes.object,
  feature: PropTypes.object,
  fields: PropTypes.object,
};

function AboutLocationContent(props) {

  const { layersAtLocation, overlaysAtLocation } = props;

  const generateFieldsMap = (fields) => {
    const fieldsMap = {};
    for (let fi = 0; fi < fields.length; fi++) {
      fieldsMap[fields[fi].name] = fields[fi]
    }
    return fieldsMap;
  };

  return (
    <SidebarBody className={styles.aboutLocation}>
			{layersAtLocation.length > 0 ? (
				<>
					<div className={styles.layerHeader}>
						<LayersIcon color={COLORS.ghOrange} style={{ marginRight: '4px' }} />
						<span>Layers</span>
					</div>
					{layersAtLocation.map((layerAtLocation, i) => {
						let { layer, metadata } = layerAtLocation
						/*
						 * If duplicate feature attributes exist, they will be removed before processing.
						 * This is a temporary solution until GIS removes them on the backend.
						 * September 27th, 2022 -JT
						 */
            let feature = null
            let filteredfeatures = []
            let index = null
            for (let i = 0; i < metadata.features.length; i++) {
              feature = metadata.features[i]
              index = filteredfeatures.findIndex(ff => ff.attributes.name === feature.attributes.name)
              if (index < 0) filteredfeatures.push(feature)
            }

						metadata.features = filteredfeatures
						const fields = generateFieldsMap(metadata.fields)

						return metadata.features.map((feature, j) => {
							return <LayerAtLocation key={`${i}_${j}-${stringToHash(JSON.stringify(feature))}`} layer={layer} feature={feature} fields={fields} />
						})
					})}
				</>
			) : null}
			{overlaysAtLocation.length > 0 ? (
				<>
					<div className={styles.layerHeader}>
						<LayersIcon color={COLORS.ghOrange} style={{ marginRight: '4px' }} />
						<span>Overlays</span>
					</div>
					{overlaysAtLocation.map((overlayAtLocation, i) => {
						let { metadata, overlay } = overlayAtLocation
						const fields = generateFieldsMap(metadata.fields)

						return metadata.features.map((feature, j) => {
							return <OverlayAtLocation key={`${i}_${j}-${stringToHash(JSON.stringify(feature))}`} overlay={overlay} feature={feature} fields={fields} />
						})
					})}
				</>
			) : null}
		</SidebarBody>
  );
}


AboutLocationContent.propTypes = {
  layersAtLocation: PropTypes.array,
  overlaysAtLocation: PropTypes.array
};

export default AboutLocationContent;
